import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { MainHeading } from "@/components/common/MainHeading"
import { TableFilters } from "@/components/common/TableFilters";
import { Layout } from "@/components/Layout/Layout"
import { useFilter } from "@/hooks/useFilter";
import CommonTable, { TableColumn, Action } from "@/components/common/CommonTable";
import { Ipayout, IPayoutResponseList } from "@/interfaces/Payouts/payout.interface";
import { GetFilterUrl, formatRand } from "@/utility/helper";
import { useEffect, useState } from "react";
import { StatusBadge } from "@/components/common/StatusBadge";
import Pagination from "@/components/common/Pagination";
import { PayoutDetailsModal } from "@/components/common/Model/PayoutDetailsModal";
import { Eye } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";

export const Payouts = () => {
    const [payouts, setPayouts] = useState<Ipayout[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState<Ipayout | null>(null);
    const { canApprovedReject } = usePermissions();
    const { filters, setPageInfo, debouncedFilters, handlePageChange, pageInfo, updateFilter } = useFilter({
        page: 1,
        limit: 10,
        search: '',
    });

    useEffect(() => {
        getPauouts();
    }, [debouncedFilters.limit, debouncedFilters.page, debouncedFilters.search]);

    const getPauouts = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IPayoutResponseList>(`${API_URL}${API_ENDPOINT.payouts}/all-requests?${queryParams}`, { method: 'GET' });
            setPayouts(response?.data?.payouts ?? []);
            console.log('Payouts response:', response);
            setPageInfo(response?.data?.pageInfo ?? { limit: 10, page: 1, total: 0, totalPages: 0 });
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const columns: TableColumn<Ipayout>[] = [
        {
            key: "payoutRef",
            header: "Payout Reference",
            render: (item) => (
                <span className="font-medium text-primary font-mono">{item.payoutRef}</span>
            )
        },
        {
            key: "amount",
            header: "Amount",
            render: (item) => (
                <span className="font-bold text-foreground">{formatRand(item.amount)}</span>
            )
        },
        {
            key: "note",
            header: "Note",
            render: (item) => (
                <span className="text-sm text-muted-foreground max-w-xs truncate">{item.note || '---'}</span>
            )
        },
        {
            key: "driver",
            header: "Driver",
            render: (item) => {
                const customerName = item.driver?.fullName || '---';
                const phoneNumber = item.driver?.phone || '---';
                return (
                    <div className="flex flex-col">
                        <span className="font-medium text-foreground">{customerName}</span>
                        <span className="text-xs text-muted-foreground">{phoneNumber}</span>
                    </div>
                );
            }
        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <StatusBadge status={item.status} />
            )
        }
    ];

    const actions: Action<Ipayout>[] = [
        {
            text: 'View Details',
            icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer" />,
            onClick: (record: Ipayout) => {
                setSelectedPayout(record);
                setIsModalOpen(true);
            },
        }
    ];

    return (
        <Layout>
            <MainHeading
                title="Payouts"
                description="Manage your payouts and view transaction history."
            />
            <div className="mt-6 flex flex-col gap-4">
                <TableFilters
                    placeholder="Search Payouts"
                    searchTerm={filters.search}
                    onSearchChange={(query) => updateFilter({ search: query })}
                />
            </div>
            <div className="mt-6 flex flex-col gap-4">
                <CommonTable
                    columns={columns}
                    data={payouts}
                    loading={loading}
                    actions={actions}
                />
                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                />
            </div>

            <PayoutDetailsModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                payout={selectedPayout}
                onSuccess={getPauouts}
            />
        </Layout>
    )
}
